import apiClient from '../client';

import { API_ENDPOINTS } from '../endpoints';

import type {

  ApiResponse,

  PaginatedResponse,

  PaginationParams,

  Tender,

  TenderFilters,

  CreateTenderRequest,

  UpdateTenderRequest,

  DashboardStats,

  Offer,

  OfferFilters,

  Client,

  ClientFilters,

  UpdateClientStatusRequest,

  Specialization,

  HomepageFeature,

  HomepageStatistic,

  HomepageContent,

  AboutValue,

  AboutContent,

  HowItWorksStep,

  HowItWorksContent,

  FAQ,

  ContactContent,

  NavigationLink,

  FooterContent,

  CreateHomepageFeatureRequest,

  UpdateHomepageFeatureRequest,

  CreateHomepageStatisticRequest,

  UpdateHomepageStatisticRequest,

  CreateHomepageContentRequest,

  CreateAboutValueRequest,

  UpdateAboutValueRequest,

  CreateAboutContentRequest,

  CreateHowItWorksStepRequest,

  UpdateHowItWorksStepRequest,

  CreateHowItWorksContentRequest,

  CreateFAQRequest,

  UpdateFAQRequest,

  CreateContactContentRequest,

  CreateNavigationLinkRequest,

  UpdateNavigationLinkRequest,

  CreateFooterContentRequest,

  CreateClientRequest,

  Settings,

  StorageSettings,

  S3Config,

  EmailSettings,

  UpdateStorageSettingsRequest,

  UpdateEmailConfigRequest,
  UpdateAllowedFileTypesRequest,

  SiteSettings,
  UpdateSiteSettingsRequest,

} from '@/api/types';

import type {
  Admin,
  AdminFilters,
  CreateAdminRequest,
  UpdateAdminRequest,
  ResetPasswordRequest,
} from '@/api/types/admin.types';



export const adminService = {

  // Dashboard

  getDashboard: async (): Promise<ApiResponse<DashboardStats>> => {

    try {

      const response = await apiClient.get<ApiResponse<DashboardStats>>(

        API_ENDPOINTS.ADMIN.DASHBOARD

      );

      return response.data;

    } catch (error: any) {

      // If 404, return empty dashboard stats

      if (error?.response?.status === 404) {

        return {

          success: false,

          data: {

            total_tenders: 0,

            active_tenders: 0,

            total_offers: 0,

            pending_offers: 0,

            accepted_offers: 0,

            total_clients: 0,

            pending_clients: 0,

            recent_tenders: [],

            recent_offers: [],

          },

          message: 'Dashboard endpoint not found',

        };

      }

      throw error;

    }

  },



  // Tenders

  getTenders: async (

    filters?: TenderFilters

  ): Promise<ApiResponse<PaginatedResponse<Tender>>> => {

    try {

      // Default sort: newest first by created_at
      const paramsWithSort = {
        ...filters,
        sort_by: filters?.sort_by || 'created_at',
        sort_order: filters?.sort_order || 'desc'
      };

      const response = await apiClient.get<any>(

        API_ENDPOINTS.ADMIN.TENDERS,

        { params: paramsWithSort }

      );

      const responseData = response.data;

      

      // Handle different response structures

      // If response has data array directly and meta

      if (Array.isArray(responseData.data) && responseData.meta) {

        return {

          success: responseData.success ?? true,

          data: {

            data: responseData.data,

            pagination: {

              page: responseData.meta.current_page || responseData.meta.page || 1,

              limit: responseData.meta.per_page || responseData.meta.limit || 15,

              total: responseData.meta.total || 0,

              totalPages: responseData.meta.last_page || responseData.meta.totalPages || 0,

            },

          },

          message: responseData.message,

        };

      }

      

      // Standard structure

      return responseData;

    } catch (error: any) {

      // If 500 or other errors, return empty list

      if (error?.response?.status >= 500) {

        return {

          success: false,

          data: {

            data: [],

            pagination: {

              page: filters?.page || 1,

              limit: filters?.limit || 10,

              total: 0,

              totalPages: 0,

            },

          },

          message: 'Error loading tenders',

        };

      }

      throw error;

    }

  },



  getTenderDetails: async (id: number): Promise<ApiResponse<Tender>> => {

    const response = await apiClient.get<ApiResponse<Tender>>(

      API_ENDPOINTS.ADMIN.TENDER_DETAIL(id)

    );

    return response.data;

  },



  createTender: async (data: CreateTenderRequest): Promise<ApiResponse<Tender>> => {

    const formData = new FormData();

    

    // Append all form fields

    Object.keys(data).forEach(key => {

      const value = data[key as keyof CreateTenderRequest];

      if (key !== 'documents' && value !== undefined) {

        if (typeof value === 'string') {

          formData.append(key, value);

        } else if (typeof value === 'number') {

          // Handle numeric fields like budget_min, budget_max

          formData.append(key, value.toString());

        } else if (typeof value === 'object' && value !== null && 'en' in value) {

          // Handle TranslatedField

          if (value.en) formData.append(`${key}[en]`, value.en);

          if (value.ar) formData.append(`${key}[ar]`, value.ar);

        } else if (Array.isArray(value)) {

          // Handle arrays like specialization_ids, client_ids
          // Use array notation to ensure backend receives them as arrays
          value.forEach(item => formData.append(`${key}[]`, item.toString()));

        }

      }

    });

    

    // Append documents if they exist

    if (data.documents && data.documents.length > 0) {

      data.documents.forEach((file, index) => {

        formData.append(`documents[${index}]`, file);

        formData.append(`document_types[${index}]`, 'requirements'); // Default type, can be extended

      });

    }

    

    const response = await apiClient.post<ApiResponse<Tender>>(

      API_ENDPOINTS.ADMIN.TENDERS,

      formData,

      {

        headers: {

          'Content-Type': 'multipart/form-data',

        },

      }

    );

    return response.data;

  },



  updateTender: async (id: number, data: UpdateTenderRequest): Promise<ApiResponse<Tender>> => {

    const response = await apiClient.put<ApiResponse<Tender>>(

      API_ENDPOINTS.ADMIN.TENDER_DETAIL(id),

      data

    );

    return response.data;

  },

  // Document Management

  addTenderDocuments: async (tenderId: number, documents: File[], documentTypes?: string[]): Promise<ApiResponse<Tender>> => {

    const formData = new FormData();

    

    // Try different approach: append files without indices first
    documents.forEach((file) => {
      formData.append('documents[]', file);
    });

    // Then append document types
    if (documentTypes) {
      documentTypes.forEach((type) => {
        formData.append('document_types[]', type);
      });
    }

    

    // Debug: log FormData contents
    console.log('Uploading documents:', documents.length);
    console.log('FormData entries:');
    for (let [key, value] of formData.entries()) {
      console.log(key, value);
    }

    const response = await apiClient.post<ApiResponse<Tender>>(

      API_ENDPOINTS.ADMIN.TENDER_DOCUMENTS(tenderId),

      formData
      // Don't set Content-Type - axios will set it automatically with correct boundary

    );

    return response.data;

  },

  deleteTenderDocument: async (tenderId: number, documentId: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.TENDER_DOCUMENT_DELETE(tenderId, documentId));

  },

  downloadTenderDocument: async (tenderId: number, documentId: number, fileName?: string): Promise<void> => {

    const response = await apiClient.get(

      API_ENDPOINTS.ADMIN.TENDER_DOCUMENT_DOWNLOAD(tenderId, documentId),

      {

        responseType: 'blob',

      }

    );

    

    // Create a blob URL and trigger download

    const blob = new Blob([response.data]);

    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');

    link.href = url;

    link.download = fileName || `document_${documentId}`;

    document.body.appendChild(link);

    link.click();

    document.body.removeChild(link);

    window.URL.revokeObjectURL(url);

  },



  deleteTender: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.TENDER_DETAIL(id));

  },



  // Offers

  getOffers: async (

    filters?: OfferFilters

  ): Promise<ApiResponse<Offer[]>> => {

    try {

      console.log('Fetching admin offers with filters:', filters);
      
      // Default sort: newest first by submitted_at
      const paramsWithSort = {
        ...filters,
        sort_by: filters?.sort_by || 'submitted_at',
        sort_order: filters?.sort_order || 'desc'
      };

      const response = await apiClient.get<ApiResponse<Offer[]>>(

        API_ENDPOINTS.ADMIN.OFFERS,

        { params: paramsWithSort }

      );

      console.log('Admin offers API response:', response.data);

      return response.data;

    } catch (error: any) {

      console.error('Admin offers API error:', error);

      console.error('Error response:', error?.response);

      

      // If 500 or other errors, return empty list

      if (error?.response?.status >= 500) {

        return {

          success: false,

          data: [],

          message: 'Error loading offers',

        };

      }

      throw error;

    }

  },



  getOfferDetails: async (id: number): Promise<ApiResponse<Offer>> => {

    const response = await apiClient.get<ApiResponse<Offer>>(

      API_ENDPOINTS.ADMIN.OFFER_DETAIL(id)

    );

    return response.data;

  },



  acceptOffer: async (id: number): Promise<ApiResponse<Offer>> => {

    const response = await apiClient.put<ApiResponse<Offer>>(

      API_ENDPOINTS.ADMIN.OFFER_DETAIL(id),

      { status: 'accepted' }

    );

    return response.data;

  },



  rejectOffer: async (id: number): Promise<ApiResponse<Offer>> => {

    const response = await apiClient.put<ApiResponse<Offer>>(

      API_ENDPOINTS.ADMIN.OFFER_DETAIL(id),

      { status: 'rejected' }

    );

    return response.data;

  },



  deleteOffer: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.OFFER_DETAIL(id));

  },



  // Clients

  getClients: async (

    filters?: ClientFilters

  ): Promise<ApiResponse<PaginatedResponse<Client>>> => {

    try {

      // Default sort: newest first by created_at
      const paramsWithSort = {
        ...filters,
        sort_by: filters?.sort_by || 'created_at',
        sort_order: filters?.sort_order || 'desc'
      };

      const response = await apiClient.get<any>(

        API_ENDPOINTS.ADMIN.CLIENTS,

        { params: paramsWithSort }

      );

      // Handle different response structures

      const responseData = response.data;

      

      // If response has data array directly and meta.pagination

      if (Array.isArray(responseData.data) && responseData.meta?.pagination) {

        return {

          success: responseData.success ?? true,

          data: {

            data: responseData.data.map((client: any) => ({

              id: client.id, // User ID (keep for reference)

              client_id: client.client_id, // CRITICAL: Client ID for API calls

              name: client.name,

              email: client.email,

              company_name: client.client?.company_name || client.company_name,

              phone: client.client?.phone || client.phone,

              address: client.client?.address || client.address,

              status: client.client?.status || client.status || 'pending',

              specialization_ids: client.client?.specialization_ids || client.specialization_ids,

              profile_image_url: client.profile_image_url,

              created_at: client.created_at,

              updated_at: client.updated_at,

            })),

            pagination: {

              page: responseData.meta.pagination.current_page || responseData.meta.pagination.page || 1,

              limit: responseData.meta.pagination.per_page || responseData.meta.pagination.limit || 15,

              total: responseData.meta.pagination.total || 0,

              totalPages: responseData.meta.pagination.last_page || responseData.meta.pagination.totalPages || Math.ceil((responseData.meta.pagination.total || 0) / (responseData.meta.pagination.per_page || responseData.meta.pagination.limit || 15)),

            },

          },

          message: responseData.message,

        };

      }

      

      // Standard structure

      return responseData;

    } catch (error: any) {

      // If 500 or other errors, return empty list

      if (error?.response?.status >= 500) {

        return {

          success: false,

          data: {

            data: [],

            pagination: {

              page: filters?.page || 1,

              limit: filters?.limit || 10,

              total: 0,

              totalPages: 0,

            },

          },

          message: 'Error loading clients',

        };

      }

      throw error;

    }

  },



  getClientDetails: async (id: number): Promise<ApiResponse<Client>> => {

    const response = await apiClient.get<ApiResponse<Client>>(

      API_ENDPOINTS.ADMIN.CLIENT_DETAIL(id)

    );

    return response.data;

  },



  updateClientStatus: async (

    id: number,

    data: UpdateClientStatusRequest

  ): Promise<ApiResponse<Client>> => {

    const response = await apiClient.put<ApiResponse<Client>>(

      API_ENDPOINTS.ADMIN.CLIENT_DETAIL(id),

      data

    );

    return response.data;

  },



  updateClient: async (id: number, data: UpdateClientRequest): Promise<ApiResponse<Client>> => {
    const formData = new FormData();

    // Append all form fields
    Object.keys(data).forEach(key => {
      const value = data[key as keyof UpdateClientRequest];

      if (key !== 'specialization_ids' && key !== 'profile_image' && key !== 'documents' && value !== undefined) {
        if (typeof value === 'string' || typeof value === 'number') {
          formData.append(key, value.toString());
        } else if (Array.isArray(value)) {
          // Handle specialization_ids array
          value.forEach(item => formData.append(key, item.toString()));
        }
      }
    });

    // Append profile image if it exists
    if (data.profile_image) {
      formData.append('profile_image', data.profile_image);
    }

    // Append specialization_ids if they exist
    if (data.specialization_ids && data.specialization_ids.length > 0) {
      data.specialization_ids.forEach((id: number) => {
        formData.append('specialization_ids[]', id.toString());
      });
    }

    // Append documents if they exist
    if (data.documents && data.documents.length > 0) {
      data.documents.forEach((file) => {
        formData.append('documents[]', file);
      });
    }

    // Append specific document types if they exist
    if (data.commercial_register) {
      formData.append('commercial_register', data.commercial_register);
    }
    if (data.tax_certificate) {
      formData.append('tax_certificate', data.tax_certificate);
    }
    if (data.iban_certificate) {
      formData.append('iban_certificate', data.iban_certificate);
    }
    if (data.zakat_income_certificate) {
      formData.append('zakat_income_certificate', data.zakat_income_certificate);
    }
    if (data.social_insurance_certificate) {
      formData.append('social_insurance_certificate', data.social_insurance_certificate);
    }
    if (data.company_profile) {
      formData.append('company_profile', data.company_profile);
    }

    const response = await apiClient.post<ApiResponse<Client>>(
      API_ENDPOINTS.ADMIN.CLIENT_PROFILE(id),
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );

    return response.data;
  },



  deleteClient: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CLIENT_DETAIL(id));

  },



  approveDocument: async (clientId: number, documentId: number, status: 'approved' | 'rejected'): Promise<ApiResponse<any>> => {

    const response = await apiClient.post<ApiResponse<any>>(

      `${API_ENDPOINTS.ADMIN.CLIENTS}/${clientId}/documents/${documentId}/approve`,

      { status }

    );

    return response.data;

  },



  createClient: async (data: CreateClientRequest): Promise<ApiResponse<Client>> => {

    const formData = new FormData();

    

    // Append all form fields

    Object.keys(data).forEach(key => {

      const value = data[key as keyof CreateClientRequest];

      if (key !== 'specialization_ids' && key !== 'profile_image' && key !== 'documents' && value !== undefined) {

        if (typeof value === 'string' || typeof value === 'number') {

          formData.append(key, value.toString());

        } else if (Array.isArray(value)) {

          // Handle specializations array

          value.forEach(item => formData.append(key, item.toString()));

        }

      }

    });

    

    // Append profile image if it exists

    if (data.profile_image) {

      formData.append('profile_image', data.profile_image);

    }

    

    // Append specialization_ids if they exist

    if (data.specialization_ids && data.specialization_ids.length > 0) {

      data.specialization_ids.forEach((id: number) => {

        formData.append('specialization_ids[]', id.toString());

      });

    }

    

    // Append documents if they exist

    if (data.documents && data.documents.length > 0) {

      data.documents.forEach((file) => {

        formData.append('documents[]', file);

      });

    }

    

    const response = await apiClient.post<ApiResponse<Client>>(

      API_ENDPOINTS.ADMIN.CLIENTS,

      formData,

      {

        headers: {

          'Content-Type': 'multipart/form-data',

        },

      }

    );

    return response.data;

  },



  // Specializations

  getSpecializations: async (): Promise<ApiResponse<Specialization[]>> => {

    const response = await apiClient.get<ApiResponse<Specialization[]>>(

      API_ENDPOINTS.ADMIN.SPECIALIZATIONS

    );

    return response.data;

  },



  // Content Management - Homepage

  getHomepageContent: async (): Promise<ApiResponse<HomepageContent>> => {

    const response = await apiClient.get<ApiResponse<HomepageContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE

    );

    return response.data;

  },



  createOrUpdateHomepageContent: async (

    data: CreateHomepageContentRequest

  ): Promise<ApiResponse<HomepageContent>> => {

    const response = await apiClient.post<ApiResponse<HomepageContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE,

      data

    );

    return response.data;

  },



  getHomepageFeatures: async (): Promise<ApiResponse<HomepageFeature[]>> => {

    const response = await apiClient.get<ApiResponse<HomepageFeature[]>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_FEATURES

    );

    return response.data;

  },



  createHomepageFeature: async (

    data: CreateHomepageFeatureRequest

  ): Promise<ApiResponse<HomepageFeature>> => {

    const response = await apiClient.post<ApiResponse<HomepageFeature>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_FEATURES,

      data

    );

    return response.data;

  },



  updateHomepageFeature: async (

    id: number,

    data: UpdateHomepageFeatureRequest

  ): Promise<ApiResponse<HomepageFeature>> => {

    const response = await apiClient.put<ApiResponse<HomepageFeature>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_FEATURE(id),

      data

    );

    return response.data;

  },



  deleteHomepageFeature: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_FEATURE(id));

  },



  getHomepageStatistics: async (): Promise<ApiResponse<HomepageStatistic[]>> => {

    const response = await apiClient.get<ApiResponse<HomepageStatistic[]>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_STATISTICS

    );

    return response.data;

  },



  createHomepageStatistic: async (

    data: CreateHomepageStatisticRequest

  ): Promise<ApiResponse<HomepageStatistic>> => {

    const response = await apiClient.post<ApiResponse<HomepageStatistic>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_STATISTICS,

      data

    );

    return response.data;

  },



  updateHomepageStatistic: async (

    id: number,

    data: UpdateHomepageStatisticRequest

  ): Promise<ApiResponse<HomepageStatistic>> => {

    const response = await apiClient.put<ApiResponse<HomepageStatistic>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_STATISTIC(id),

      data

    );

    return response.data;

  },



  deleteHomepageStatistic: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_STATISTIC(id));

  },



  // Content Management - About

  getAboutContent: async (): Promise<ApiResponse<AboutContent>> => {

    const response = await apiClient.get<ApiResponse<AboutContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.ABOUT

    );

    return response.data;

  },



  createOrUpdateAboutContent: async (

    data: CreateAboutContentRequest

  ): Promise<ApiResponse<AboutContent>> => {

    const response = await apiClient.post<ApiResponse<AboutContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.ABOUT,

      data

    );

    return response.data;

  },



  getAboutValues: async (): Promise<ApiResponse<AboutValue[]>> => {

    const response = await apiClient.get<ApiResponse<AboutValue[]>>(

      API_ENDPOINTS.ADMIN.CONTENT.ABOUT_VALUES

    );

    return response.data;

  },



  createAboutValue: async (

    data: CreateAboutValueRequest

  ): Promise<ApiResponse<AboutValue>> => {

    const response = await apiClient.post<ApiResponse<AboutValue>>(

      API_ENDPOINTS.ADMIN.CONTENT.ABOUT_VALUES,

      data

    );

    return response.data;

  },



  updateAboutValue: async (

    id: number,

    data: UpdateAboutValueRequest

  ): Promise<ApiResponse<AboutValue>> => {

    const response = await apiClient.put<ApiResponse<AboutValue>>(

      API_ENDPOINTS.ADMIN.CONTENT.ABOUT_VALUE(id),

      data

    );

    return response.data;

  },



  deleteAboutValue: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CONTENT.ABOUT_VALUE(id));

  },



  // Content Management - How It Works

  getHowItWorksContent: async (): Promise<ApiResponse<HowItWorksContent>> => {

    const response = await apiClient.get<ApiResponse<HowItWorksContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOW_IT_WORKS

    );

    return response.data;

  },



  createOrUpdateHowItWorksContent: async (

    data: CreateHowItWorksContentRequest

  ): Promise<ApiResponse<HowItWorksContent>> => {

    const response = await apiClient.post<ApiResponse<HowItWorksContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOW_IT_WORKS,

      data

    );

    return response.data;

  },



  getHowItWorksSteps: async (): Promise<ApiResponse<HowItWorksStep[]>> => {

    const response = await apiClient.get<ApiResponse<HowItWorksStep[]>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOW_IT_WORKS_STEPS

    );

    return response.data;

  },



  createHowItWorksStep: async (

    data: CreateHowItWorksStepRequest

  ): Promise<ApiResponse<HowItWorksStep>> => {

    const response = await apiClient.post<ApiResponse<HowItWorksStep>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOW_IT_WORKS_STEPS,

      data

    );

    return response.data;

  },



  updateHowItWorksStep: async (

    id: number,

    data: UpdateHowItWorksStepRequest

  ): Promise<ApiResponse<HowItWorksStep>> => {

    const response = await apiClient.put<ApiResponse<HowItWorksStep>>(

      API_ENDPOINTS.ADMIN.CONTENT.HOW_IT_WORKS_STEP(id),

      data

    );

    return response.data;

  },



  deleteHowItWorksStep: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CONTENT.HOW_IT_WORKS_STEP(id));

  },



  // Content Management - FAQs

  getFAQs: async (): Promise<ApiResponse<FAQ[]>> => {

    const response = await apiClient.get<ApiResponse<FAQ[]>>(

      API_ENDPOINTS.ADMIN.CONTENT.FAQS

    );

    return response.data;

  },



  createFAQ: async (data: CreateFAQRequest): Promise<ApiResponse<FAQ>> => {

    const response = await apiClient.post<ApiResponse<FAQ>>(

      API_ENDPOINTS.ADMIN.CONTENT.FAQS,

      data

    );

    return response.data;

  },



  updateFAQ: async (

    id: number,

    data: UpdateFAQRequest

  ): Promise<ApiResponse<FAQ>> => {

    const response = await apiClient.put<ApiResponse<FAQ>>(

      API_ENDPOINTS.ADMIN.CONTENT.FAQ(id),

      data

    );

    return response.data;

  },



  deleteFAQ: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CONTENT.FAQ(id));

  },



  // Content Management - Contact

  getContactContent: async (): Promise<ApiResponse<ContactContent>> => {

    const response = await apiClient.get<ApiResponse<ContactContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.CONTACT

    );

    return response.data;

  },



  createOrUpdateContactContent: async (

    data: CreateContactContentRequest

  ): Promise<ApiResponse<ContactContent>> => {

    const response = await apiClient.post<ApiResponse<ContactContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.CONTACT,

      data

    );

    return response.data;

  },



  // Content Management - Navigation

  getNavigationLinks: async (): Promise<ApiResponse<NavigationLink[]>> => {

    const response = await apiClient.get<ApiResponse<NavigationLink[]>>(

      API_ENDPOINTS.ADMIN.CONTENT.NAVIGATION

    );

    return response.data;

  },



  createNavigationLink: async (

    data: CreateNavigationLinkRequest

  ): Promise<ApiResponse<NavigationLink>> => {

    const response = await apiClient.post<ApiResponse<NavigationLink>>(

      API_ENDPOINTS.ADMIN.CONTENT.NAVIGATION,

      data

    );

    return response.data;

  },



  updateNavigationLink: async (

    id: number,

    data: UpdateNavigationLinkRequest

  ): Promise<ApiResponse<NavigationLink>> => {

    const response = await apiClient.put<ApiResponse<NavigationLink>>(

      API_ENDPOINTS.ADMIN.CONTENT.NAVIGATION_LINK(id),

      data

    );

    return response.data;

  },



  deleteNavigationLink: async (id: number): Promise<void> => {

    await apiClient.delete(API_ENDPOINTS.ADMIN.CONTENT.NAVIGATION_LINK(id));

  },



  // Content Management - Footer

  getFooterContent: async (): Promise<ApiResponse<FooterContent>> => {

    const response = await apiClient.get<ApiResponse<FooterContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.FOOTER

    );

    return response.data;

  },



  createOrUpdateFooterContent: async (

    data: CreateFooterContentRequest

  ): Promise<ApiResponse<FooterContent>> => {

    const response = await apiClient.post<ApiResponse<FooterContent>>(

      API_ENDPOINTS.ADMIN.CONTENT.FOOTER,

      data

    );

    return response.data;

  },



  // Settings Management

  getAllSettings: async (): Promise<ApiResponse<Settings>> => {

    const response = await apiClient.get<ApiResponse<Settings>>(

      API_ENDPOINTS.ADMIN.SETTINGS

    );

    return response.data;

  },



  updateSetting: async (

    key: string,

    data: UpdateSettingRequest

  ): Promise<ApiResponse<{ message: string }>> => {

    const response = await apiClient.put<ApiResponse<{ message: string }>>(

      API_ENDPOINTS.ADMIN.SETTING_DETAIL(key),

      data

    );

    return response.data;

  },



  getStorageSettings: async (): Promise<ApiResponse<StorageSettings>> => {

    const response = await apiClient.get<ApiResponse<StorageSettings>>(

      API_ENDPOINTS.ADMIN.SETTINGS_STORAGE

    );

    return response.data;

  },



  updateStorageSettings: async (

    data: UpdateStorageSettingsRequest

  ): Promise<ApiResponse<{ message: string }>> => {

    const response = await apiClient.put<ApiResponse<{ message: string }>>(

      API_ENDPOINTS.ADMIN.SETTINGS_STORAGE,

      data

    );

    return response.data;

  },



  updateS3Config: async (data: S3Config): Promise<ApiResponse<{ message: string }>> => {

    const response = await apiClient.put<ApiResponse<{ message: string }>>(

      API_ENDPOINTS.ADMIN.SETTINGS_STORAGE_S3,

      data

    );

    return response.data;

  },



  updateAllowedFileTypes: async (

    data: UpdateAllowedFileTypesRequest

  ): Promise<ApiResponse<{ allowed_file_types: string[]; message: string }>> => {

    const response = await apiClient.put<ApiResponse<{ allowed_file_types: string[]; message: string }>>(

      API_ENDPOINTS.ADMIN.SETTINGS_STORAGE_FILE_TYPES,

      data

    );

    return response.data;

  },



  getEmailSettings: async (): Promise<ApiResponse<EmailSettings>> => {

    const response = await apiClient.get<ApiResponse<EmailSettings>>(

      API_ENDPOINTS.ADMIN.SETTINGS_EMAIL

    );

    return response.data;

  },



  updateEmailConfig: async (

    data: UpdateEmailConfigRequest

  ): Promise<ApiResponse<{ message: string }>> => {

    const response = await apiClient.put<ApiResponse<{ message: string }>>(

      API_ENDPOINTS.ADMIN.SETTINGS_EMAIL,

      data

    );

    return response.data;

  },


  // Site Settings
  getSiteSettings: async (): Promise<ApiResponse<SiteSettings>> => {
    const response = await apiClient.get<ApiResponse<SiteSettings>>(
      API_ENDPOINTS.ADMIN.SITE_SETTINGS
    );
    return response.data;
  },

  updateSiteSettings: async (data: UpdateSiteSettingsRequest): Promise<ApiResponse<SiteSettings>> => {
    const response = await apiClient.put<ApiResponse<SiteSettings>>(
      API_ENDPOINTS.ADMIN.SITE_SETTINGS,
      data
    );
    return response.data;
  },

  uploadLogo: async (file: File): Promise<ApiResponse<{ logo: string }>> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiClient.post<ApiResponse<{ logo: string }>>(
      API_ENDPOINTS.ADMIN.SITE_SETTINGS_LOGO,
      formData
      // Don't set Content-Type - axios will set it automatically with correct boundary
    );
    return response.data;
  },

  uploadLogoDark: async (file: File): Promise<ApiResponse<{ logo_dark: string }>> => {
    const formData = new FormData();
    formData.append('logo', file);
    const response = await apiClient.post<ApiResponse<{ logo_dark: string }>>(
      API_ENDPOINTS.ADMIN.SITE_SETTINGS_LOGO_DARK,
      formData
    );
    return response.data;
  },

  uploadFavicon: async (file: File): Promise<ApiResponse<{ favicon: string }>> => {
    const formData = new FormData();
    formData.append('favicon', file);
    const response = await apiClient.post<ApiResponse<{ favicon: string }>>(
      API_ENDPOINTS.ADMIN.SITE_SETTINGS_FAVICON,
      formData
    );
    return response.data;
  },

  deleteLogo: async (): Promise<ApiResponse<{ message: string }>> => {
    const response = await apiClient.delete<ApiResponse<{ message: string }>>(
      API_ENDPOINTS.ADMIN.SITE_SETTINGS_LOGO
    );
    return response.data;
  },

  // Hero Background Upload
  uploadHeroBackground: async (file: File): Promise<ApiResponse<{ hero_background_image: string; url: string }>> => {
    const formData = new FormData();
    formData.append('hero_background', file);
    const response = await apiClient.post<ApiResponse<{ hero_background_image: string; url: string }>>(
      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_HERO_BACKGROUND,
      formData
    );
    return response.data;
  },

  deleteHeroBackground: async (): Promise<ApiResponse<null>> => {
    const response = await apiClient.delete<ApiResponse<null>>(
      API_ENDPOINTS.ADMIN.CONTENT.HOMEPAGE_HERO_BACKGROUND
    );
    return response.data;
  },

  // Admin Users Management (Super Admin Only)
  getAdmins: async (
    filters?: AdminFilters
  ): Promise<ApiResponse<PaginatedResponse<Admin>>> => {
    const response = await apiClient.get<ApiResponse<PaginatedResponse<Admin>>>(
      API_ENDPOINTS.ADMIN.ADMINS,
      { params: filters }
    );
    return response.data;
  },

  getAdmin: async (id: number): Promise<ApiResponse<Admin>> => {
    const response = await apiClient.get<ApiResponse<Admin>>(
      API_ENDPOINTS.ADMIN.ADMIN_DETAIL(id)
    );
    return response.data;
  },

  createAdmin: async (data: CreateAdminRequest): Promise<ApiResponse<Admin>> => {
    const response = await apiClient.post<ApiResponse<Admin>>(
      API_ENDPOINTS.ADMIN.ADMINS,
      data
    );
    return response.data;
  },

  updateAdmin: async (id: number, data: UpdateAdminRequest): Promise<ApiResponse<Admin>> => {
    const response = await apiClient.put<ApiResponse<Admin>>(
      API_ENDPOINTS.ADMIN.ADMIN_DETAIL(id),
      data
    );
    return response.data;
  },

  deleteAdmin: async (id: number): Promise<ApiResponse<null>> => {
    const response = await apiClient.delete<ApiResponse<null>>(
      API_ENDPOINTS.ADMIN.ADMIN_DETAIL(id)
    );
    return response.data;
  },

  resetAdminPassword: async (id: number, data: ResetPasswordRequest): Promise<ApiResponse<Admin>> => {
    const response = await apiClient.post<ApiResponse<Admin>>(
      API_ENDPOINTS.ADMIN.ADMIN_RESET_PASSWORD(id),
      data
    );
    return response.data;
  },

};

